import React, { useCallback, useEffect, useMemo, useState } from "react";
import { relevanceFromDistance } from "../utils";
import { SearchIcon } from "./Icons";
import { Pagination } from "./Pagination";
import { RelevanceAudienceResults } from "./tiledAudienceResults";

// Download icon for export button
const DownloadIcon = () => (
	<svg
		width="16"
		height="16"
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		strokeWidth="2"
	>
		<path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
		<polyline points="7,10 12,15 17,10"></polyline>
		<line x1="12" y1="15" x2="12" y2="3"></line>
	</svg>
);

// Chevron down icon for dropdown
const ChevronDownIcon = () => (
	<svg
		width="16"
		height="16"
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		strokeWidth="2"
	>
		<polyline points="6,9 12,15 18,9"></polyline>
	</svg>
);

const ViewToggleIcon = ({ isTiled }) => (
	<svg
		width="16"
		height="16"
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		strokeWidth="2"
	>
		{isTiled ? (
			// Table view icon
			<>
				<line x1="8" y1="6" x2="21" y2="6"></line>
				<line x1="8" y1="12" x2="21" y2="12"></line>
				<line x1="8" y1="18" x2="21" y2="18"></line>
				<line x1="3" y1="6" x2="3.01" y2="6"></line>
				<line x1="3" y1="12" x2="3.01" y2="12"></line>
				<line x1="3" y1="18" x2="3.01" y2="18"></line>
			</>
		) : (
			// Grid view icon
			<>
				<rect x="3" y="3" width="7" height="7"></rect>
				<rect x="14" y="3" width="7" height="7"></rect>
				<rect x="3" y="14" width="7" height="7"></rect>
				<rect x="14" y="14" width="7" height="7"></rect>
			</>
		)}
	</svg>
);

// Sort Icon Component
const SortIcon = ({ column, currentSortColumn, sortDirection }) => {
	const isActive = currentSortColumn === column;

	return (
		<span className="sort-icon">
			<svg
				width="12"
				height="12"
				viewBox="0 0 24 24"
				fill="none"
				stroke="currentColor"
				strokeWidth="2"
			>
				<path
					d="M7 14l5-5 5 5"
					fill={isActive && sortDirection === "asc" ? "currentColor" : "none"}
					stroke={isActive && sortDirection === "asc" ? "currentColor" : "#ccc"}
					strokeWidth={isActive && sortDirection === "asc" ? 2 : 1}
				/>
				<path
					d="M7 10l5 5 5-5"
					fill={isActive && sortDirection === "desc" ? "currentColor" : "none"}
					stroke={
						isActive && sortDirection === "desc" ? "currentColor" : "#ccc"
					}
					strokeWidth={isActive && sortDirection === "desc" ? 2 : 1}
				/>
			</svg>
		</span>
	);
};

// Export Dropdown Component for Audience Results
const EnhancedAudienceExportDropdown = ({
	filteredData,
	isExporting,
	onExport,
	totalCount,
	selectedCount = 0,
}) => {
	const [isOpen, setIsOpen] = useState(false);

	return (
		<div className="export-dropdown" style={{ position: "relative" }}>
			<button
				onClick={() => setIsOpen(!isOpen)}
				className="btn-medium-secondary"
				disabled={isExporting || !filteredData || filteredData.length === 0}
				style={{
					display: "flex",
					alignItems: "center",
					gap: "8px",
				}}
			>
				<DownloadIcon />
				{isExporting ? "Exporting..." : "Export Results"}
				<ChevronDownIcon />
			</button>

			{isOpen && (
				<div
					className="dropdown-menu"
					style={{
						position: "absolute",
						top: "100%",
						right: 0,
						backgroundColor: "white",
						border: "1px solid #dee2e6",
						borderRadius: "4px",
						boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
						zIndex: 1000,
						minWidth: "200px",
						marginTop: "4px",
					}}
				>
					<button
						onClick={() => {
							onExport("summarized");
							setIsOpen(false);
						}}
						className="dropdown-item"
						onMouseEnter={(e) => (e.target.style.backgroundColor = "#f8f9fa")}
						onMouseLeave={(e) =>
							(e.target.style.backgroundColor = "transparent")
						}
					>
						<div>
							<div style={{ fontWeight: "500" }}>Export Summarized</div>
						</div>
					</button>

					<button
						onClick={() => {
							onExport("detailed");
							setIsOpen(false);
						}}
						className="dropdown-item"
						onMouseEnter={(e) => (e.target.style.backgroundColor = "#f8f9fa")}
						onMouseLeave={(e) =>
							(e.target.style.backgroundColor = "transparent")
						}
					>
						<div>
							<div style={{ fontWeight: "500" }}>Export Detailed</div>
						</div>
					</button>
				</div>
			)}
		</div>
	);
};

export const AudienceResultsList = ({
	data,
	alldata,
	selectedResults,
	onResultSelect,
	// ✅ Keep these props for compatibility but manage pagination internally
	currentPage: externalCurrentPage,
	totalPages: externalTotalPages,
	totalItems: externalTotalItems,
	itemsPerPage: externalItemsPerPage = 25,
	onPageChange,
	onPageSizeChange,
	onSelectAll,
	isAllSelected,
	isExporting,
	onExport,
	searchTerm,
	onSearchChange,
	isLoading = false,
	error = null,
	isPaginatedData = false,
	onExcludeSelected,
	excludedItems = [],
	// ✅ NEW: Add filtering props
	includeTerms = [],
	excludeTerms = [],
	// ✅ NEW: Add fullDataset prop like AppResultsList
	fullDataset = [],
	onOpenAudienceModal = null,
}) => {
	const [activeTooltip, setActiveTooltip] = useState(null);
	const [tooltipPosition, setTooltipPosition] = useState({
		top: true,
		left: 0,
	});
	const [isSearchVisible, setIsSearchVisible] = useState(false);
	const [viewMode, setViewMode] = useState("tiled"); // Default to tiled view
	const [sortColumn, setSortColumn] = useState(null);
	const [sortDirection, setSortDirection] = useState("asc"); // 'asc' or 'desc'

	// ✅ Internal pagination state - like AppResultsList
	const [internalCurrentPage, setInternalCurrentPage] = useState(1);
	const [internalPageSize, setInternalPageSize] =
		useState(externalItemsPerPage);
	const [hoveredAudienceName, setHoveredAudienceName] = useState(null);
	const [nameTooltipPosition, setNameTooltipPosition] = useState({
		top: 0,
		left: 0,
	});

	const getFilteredDataByTerms = useMemo(() => {
		console.log(
			"AUDIENCE FILTERING - Safe mode: show all except excluded terms:",
			{
				dataType: typeof data,
				dataIsArray: Array.isArray(data),
				dataLength: Array.isArray(data) ? data.length : "not array",
				includeTerms,
				excludeTerms,
			},
		);

		if (!data || !Array.isArray(data)) {
			console.log("Data is not an array, returning empty array");
			return [];
		}

		// Start with all data - no filtering on include terms
		let filteredData = [...data];

		// Extract exclude term strings
		const excludeTermStrings =
			excludeTerms?.map((term) => term.query || term.text || term) || [];

		console.log("Exclude terms to filter out:", excludeTermStrings);

		// ONLY apply exclude filters - remove items that match exclude terms
		if (excludeTermStrings.length > 0) {
			const beforeCount = filteredData.length;

			const itemMatchesExcludeTerm = (item, term) => {
				if (!item || !term) return false;

				const termLower = term.toLowerCase();

				const searchableFields = [
					item.audienceVector?.audienceName,
					item.audienceVector?.cleanAudienceName,
					item.audienceVector?.audienceStrategy,
					item.audienceVector?.primaryPersona,
					item.audienceVector?.description,
					item.audienceVector?.notesDescription,
					item.audienceVector?.technicalAttributes,
					item.audienceVector?.createdWith,
					item.audienceName,
					item.primaryPersona,
					item.audienceStrategy,
					item.description,
					item.accountName,
				].filter(Boolean);

				return searchableFields.some((field) =>
					String(field).toLowerCase().includes(termLower),
				);
			};

			filteredData = filteredData.filter((item) => {
				// Keep items that DON'T match any exclude terms
				const shouldExclude = excludeTermStrings.some((term) =>
					itemMatchesExcludeTerm(item, term),
				);
				return !shouldExclude;
			});

			console.log(
				`Excluded ${beforeCount - filteredData.length} items matching exclude terms. Remaining: ${filteredData.length}`,
			);
		} else {
			console.log("No exclude terms - showing all data");
		}

		// NOTE: Include terms are ignored for filtering - they're just for UI display
		if (includeTerms?.length > 0) {
			console.log(
				"Include terms present but not used for filtering:",
				includeTerms.map((t) => t.text || t.query),
			);
		}

		console.log("AUDIENCE FILTERING - Final result (safe mode):", {
			originalCount: data.length,
			finalCount: filteredData.length,
			excludedTerms: excludeTermStrings,
			includeTermsIgnored: includeTerms?.length || 0,
		});

		return filteredData;
	}, [data, includeTerms, excludeTerms]);

	const completeDataset = getFilteredDataByTerms;

	console.log("🎯 AUDIENCE RESULTS LIST - Component props:", {
		dataType: typeof data,
		dataIsArray: Array.isArray(data),
		dataLength: Array.isArray(data) ? data.length : "not array",
		filteredDataLength: completeDataset.length,
		includeTerms,
		excludeTerms,
		firstItem: completeDataset?.[0],
		firstItemKeys: completeDataset?.[0]
			? Object.keys(completeDataset[0])
			: "no first item",
	});

	// ✅ FIXED: Improved data sanitization that handles the actual API response structure
	const sanitizeAudienceItem = (item, index) => {
		try {
			if (!item || typeof item !== "object") {
				console.warn(`⚠️ Invalid item at index ${index}:`, item);
				return createFallbackItem(index);
			}

			const audienceVector = item.audienceVector || {};
			const audienceMetrics = item.audienceMetrics || {};

			// Extract audience name - prioritize cleanAudienceName
			const audienceName =
				audienceVector.cleanAudienceName ||
				audienceVector.audienceName ||
				audienceVector.name ||
				item.audienceName ||
				`Audience ${index + 1}`;

			// Extract audience ID
			const audienceId =
				audienceVector.audienceId ||
				item.audienceId ||
				item.id ||
				`audience_${index + 1}`;

			const sanitizedItem = {
				// Basic identifiers
				id: audienceId,
				audienceId: audienceId,
				audienceName: audienceName,
				accountName: audienceVector.accountName || "Unknown Account",

				// Scores from the main audience object
				compositeScore: item.compositeScore || 0,
				score: typeof item.score === "number" ? item.score : null,

				// Audience properties from audienceVector
				region: audienceVector.region || "N/A",
				audienceStrategy: audienceVector.audienceStrategy || "N/A", // This is the strategy
				type: audienceVector.type || "N/A", // This might be "v2" in your data
				dataSource: audienceVector.dataSource || "N/A",
				status: audienceVector.status || "PROCESSED",
				description:
					audienceVector.notesDescription ||
					audienceVector.description ||
					"N/A",

				// Handle exportTargets array properly
				exportTargets: (() => {
					const targets = audienceVector.exportTargets;
					if (!targets) return "N/A";

					// Handle array
					if (Array.isArray(targets)) {
						return targets.join(", ");
					}

					// Handle JSON string like "[\"ctv\",\"cookie\"]"
					if (typeof targets === "string") {
						try {
							const parsed = JSON.parse(targets);
							if (Array.isArray(parsed)) {
								return parsed.join(", ");
							}
							return targets.replace(/[[\]"]/g, "").replace(/,/g, ", ");
						} catch (e) {
							// If JSON parse fails, clean up the string
							return targets.replace(/[[\]"]/g, "").replace(/,/g, ", ");
						}
					}

					return String(targets);
				})(),

				technicalAttributes: audienceVector.technicalAttributes || "N/A",
				createdWith: audienceVector.createdWith || "N/A",
				primaryPersona:
					audienceVector.primaryPersona ||
					audienceVector.audienceStrategy ||
					"N/A",

				// Metrics from audienceMetrics with CORRECT mappings
				metrics: {
					// Core metrics
					uniqueUsers: audienceMetrics.uniqueUsers || 0,
					requests: audienceMetrics.requests || 0, // This is what you're showing as "Impressions"
					soldImpressions: audienceMetrics.soldImpressions || 0,
					advertiserSpend: audienceMetrics.advertiserSpend || 0,
					avgEcpm: audienceMetrics.avgEcpm || 0,
					avgSpendPerUser: audienceMetrics.avgSpendPerUniqueUser || 0, // FIXED: was avgSpendPerUser
					spendInUsd: audienceMetrics.spendInUsd || 0,

					// Device metrics
					cookies: audienceMetrics.cookies || 0,
					mobileDevices: audienceMetrics.mobileDevices || 0,
					connectedTvDevices: audienceMetrics.connectedTvDevices || 0,
					tabletDevices: audienceMetrics.tabletDevices || 0,
					desktopsWithRequests: audienceMetrics.desktopsWithRequests || 0,

					// Engagement metrics
					clicks: audienceMetrics.clicks || 0,
					clickThroughRate: audienceMetrics.clickThroughRate || 0,
					viewability: audienceMetrics.viewability || 0,

					// Fill and impression metrics
					fills: audienceMetrics.fills || 0,
					impressions: audienceMetrics.impressions || 0, // Separate from requests
					viewableImpressions: audienceMetrics.viewables || 0, // FIXED: was viewableImpressions

					// Video metrics - FIXED mappings
					videoStarts: audienceMetrics.startFirstOccurrence || 0, // FIXED: was videoStarts
					videoCompletions: audienceMetrics.completeFirstOccurrence || 0, // FIXED: was videoCompletions

					// Additional metrics
					impressionFrequency: audienceMetrics.impressionFrequency || 0,
				},

				// Additional data
				createdAt: audienceVector.createdAt || audienceVector.createdDate,
				updatedAt: audienceVector.updatedDate || audienceVector.updated_date,
				isArchived: audienceVector.isArchived || false,

				// Keep original data for reference
				originalData: item,
				audienceVector: audienceVector,
				audienceMetrics: audienceMetrics,
			};

			return sanitizedItem;
		} catch (error) {
			console.error(`❌ Error sanitizing audience item ${index}:`, error, item);
			return createFallbackItem(index);
		}
	};

	const createFallbackItem = (index) => {
		return {
			id: `fallback_${index + 1}`,
			audienceId: `fallback_${index + 1}`,
			audienceName: `Audience ${index + 1}`,
			accountName: "Unknown Account",
			compositeScore: 0,
			score: 0,
			region: "N/A",
			audienceStrategy: "N/A",
			type: "N/A",
			dataSource: "N/A",
			status: "UNKNOWN",
			description: "N/A",
			exportTargets: "N/A",
			technicalAttributes: "N/A",
			createdWith: "N/A",
			metrics: {
				uniqueUsers: 0,
				requests: 0,
				soldImpressions: 0,
				cookies: 0,
				mobileDevices: 0,
				connectedTvDevices: 0,
				advertiserSpend: 0,
				spendInUsd: 0,
				avgEcpm: 0,
				clickThroughRate: 0,
				viewability: 0,
				desktopsWithRequests: 0,
				tabletDevices: 0,
				clicks: 0,
				fills: 0,
				videoStarts: 0,
				videoCompletions: 0,
				viewableImpressions: 0,
				avgSpendPerUser: 0,
			},
		};
	};

	// ✅ FIXED: Improved data validation and sanitization
	const validateAndSanitizeData = useMemo(() => {
		console.log("📄 AUDIENCE RESULTS LIST - Validating and sanitizing data:", {
			dataType: typeof completeDataset,
			dataIsArray: Array.isArray(completeDataset),
			dataLength: completeDataset?.length,
		});

		if (!Array.isArray(completeDataset)) {
			console.warn("⚠️ CompleteDataset is not an array:", completeDataset);
			return [];
		}

		const sanitizedData = completeDataset
			.map((item, index) => {
				if (!item || typeof item !== "object") {
					console.warn(`⚠️ Invalid item at index ${index}:`, item);
					return createFallbackItem(index);
				}
				return sanitizeAudienceItem(item, index);
			})
			.filter(Boolean)
			.filter((item) => !excludedItems.includes(item.id));

		console.log("✅ AUDIENCE RESULTS LIST - Sanitization complete:", {
			originalCount: completeDataset.length,
			sanitizedCount: sanitizedData.length,
			excludedCount: excludedItems.length,
			firstSanitizedItem: sanitizedData[0],
		});

		return sanitizedData;
	}, [completeDataset, excludedItems]);

	const calculateRelevanceScore = (audience) => {
		// Method 1: If there's already a relevance score, use it
		if (
			audience.relevanceScore &&
			typeof audience.relevanceScore === "number"
		) {
			return audience.relevanceScore;
		}

		// Method 2: If there's a score field, transform it
		if (typeof audience.score === "number") {
			// Audience sigmoid relevance curve (see constants.js)
			const score = parseFloat(audience.score);
			return relevanceFromDistance(score, "AUDIENCE");
		}
	};

	const toggleViewMode = () => {
		setViewMode(viewMode === "tiled" ? "table" : "tiled");
	};

	// Sorting functionality
	const handleSort = (column) => {
		if (sortColumn === column) {
			// Toggle direction if same column
			setSortDirection(sortDirection === "asc" ? "desc" : "asc");
		} else {
			// New column, start with ascending
			setSortColumn(column);
			setSortDirection("asc");
		}
	};

	const getSortValue = (item, column) => {
		switch (column) {
			case "audienceName":
				return item.audienceName?.toLowerCase() || "";
			case "organization":
				return item.accountName?.toLowerCase() || "";
			case "provider":
				return item.dataSource?.toLowerCase() || "";
			case "compositeScore":
				return parseFloat(item.compositeScore) || 0;
			case "searchScore":
				return parseFloat(calculateRelevanceScore(item)) || 0;
			case "reach":
				return parseInt(item.metrics?.uniqueUsers) || 0;
			case "soldImpressions":
				return parseInt(item.metrics?.soldImpressions) || 0;
			case "avgEcpm":
				return parseFloat(item.metrics?.avgEcpm) || 0;
			case "primaryPersona":
				return item.primaryPersona?.toLowerCase() || "";
			case "audienceStrategy":
				return item.audienceStrategy?.toLowerCase() || "";
			case "status":
				return item.status?.toLowerCase() || "";
			case "mediaTypes":
				return item.exportTargets?.toLowerCase() || "";
			case "notes":
				return item.description?.toLowerCase() || "";
			case "clicks":
				return parseInt(item.metrics?.clicks) || 0;
			case "impressions":
				return parseInt(item.metrics?.impressions) || 0; // Using requests for impressions column
			case "fills":
				return parseInt(item.metrics?.fills) || 0;
			case "videoStarts":
				return parseInt(item.metrics?.videoStarts) || 0; // This will be startFirstOccurrence
			case "videoCompletions":
				return parseInt(item.metrics?.videoCompletions) || 0; // This will be completeFirstOccurrence
			case "viewableImpressions":
				return parseInt(item.metrics?.viewableImpressions) || 0; // This will be viewables
			case "spend":
				return parseFloat(item.metrics?.spendInUsd) || 0;
			case "avgSpendPerUser":
				return parseFloat(item.metrics?.avgSpendPerUser) || 0; // This will be avgSpendPerUniqueUser
			default:
				return "";
		}
	};

	const sortData = (data) => {
		if (!sortColumn) return data;

		return [...data].sort((a, b) => {
			const aValue = getSortValue(a, sortColumn);
			const bValue = getSortValue(b, sortColumn);

			let comparison = 0;

			if (typeof aValue === "string" && typeof bValue === "string") {
				comparison = aValue.localeCompare(bValue);
			} else {
				comparison = aValue - bValue;
			}

			return sortDirection === "desc" ? -comparison : comparison;
		});
	};

	// ✅ NEW: Filtering and sorting logic - like AppResultsList
	const getFilteredAndSortedData = () => {
		const datasetToUse =
			fullDataset.length > 0 ? fullDataset : validateAndSanitizeData;

		let filteredData = datasetToUse;

		// Apply search filter
		if (searchTerm && searchTerm.trim()) {
			const searchLower = searchTerm.toLowerCase();
			filteredData = filteredData.filter(
				(audience) =>
					audience.audienceName.toLowerCase().includes(searchLower) ||
					audience.accountName.toLowerCase().includes(searchLower) ||
					audience.audienceStrategy.toLowerCase().includes(searchLower) ||
					audience.region.toLowerCase().includes(searchLower) ||
					audience.dataSource.toLowerCase().includes(searchLower),
			);
		}

		// Apply sorting
		return sortData(filteredData);
	};

	// ✅ NEW: Pagination calculation - like AppResultsList
	const getPaginatedData = () => {
		const sortedFilteredData = getFilteredAndSortedData();
		const currentPage = internalCurrentPage;
		const pageSize = internalPageSize;
		const startIndex = (currentPage - 1) * pageSize;
		const endIndex = startIndex + pageSize;
		return sortedFilteredData.slice(startIndex, endIndex);
	};

	// ✅ NEW: Calculate totals - like AppResultsList
	const getCalculatedTotalPages = () => {
		const filteredData = getFilteredAndSortedData();
		return Math.ceil(filteredData.length / internalPageSize);
	};

	const getCalculatedTotalItems = () => {
		const filteredData = getFilteredAndSortedData();
		return filteredData.length;
	};

	// ✅ NEW: Handle page changes internally - like AppResultsList
	const handleInternalPageChange = useCallback(
		(newPage) => {
			console.log("📄 AUDIENCE PAGINATION - Internal page change:", {
				from: internalCurrentPage,
				to: newPage,
				maxPages: getCalculatedTotalPages(),
			});

			setInternalCurrentPage(newPage);

			// Also call external handler if provided (for compatibility)
			if (onPageChange) {
				onPageChange(newPage);
			}
		},
		[internalCurrentPage, onPageChange],
	);

	const handleInternalPageSizeChange = useCallback(
		(newSize) => {
			console.log("📄 AUDIENCE PAGINATION - Page size change:", {
				from: internalPageSize,
				to: newSize,
			});

			setInternalPageSize(newSize);
			setInternalCurrentPage(1); // Reset to page 1

			// Also call external handler if provided (for compatibility)
			if (onPageSizeChange) {
				onPageSizeChange(newSize);
			}
		},
		[internalPageSize, onPageSizeChange],
	);

	const calculateNameTooltipPosition = (element) => {
		if (!element) return { top: 0, left: 0 };

		const rect = element.getBoundingClientRect();
		const tooltipWidth = 400; // max-width from CSS
		const viewportWidth = window.innerWidth;

		// Position below the element
		const top = rect.bottom + 5; // 5px below
		let left = rect.left;

		// Adjust if tooltip would go off-screen to the right
		if (left + tooltipWidth > viewportWidth) {
			left = viewportWidth - tooltipWidth - 10; // 10px margin from edge
		}

		// Ensure it doesn't go off-screen to the left
		if (left < 10) {
			left = 10;
		}

		return {
			top: top,
			left: left,
		};
	};

	// ✅ Use calculated values instead of external props
	const displayTotalItems = getCalculatedTotalItems();
	const displayTotalPages = getCalculatedTotalPages();
	const currentPageData = getPaginatedData();

	// Event handlers and other functions remain the same...
	useEffect(() => {
		const handleClickOutside = (event) => {
			if (
				!event.target.closest(".info-icon-wrapper") &&
				!event.target.closest(".metrics-tooltip")
			) {
				setActiveTooltip(null);
			}
		};

		if (activeTooltip) {
			document.addEventListener("click", handleClickOutside);
		}

		return () => {
			document.removeEventListener("click", handleClickOutside);
		};
	}, [activeTooltip]);

	const calculateTooltipPosition = (buttonElement) => {
		if (!buttonElement)
			return {
				top: true,
				left: 100,
				right: "auto",
				buttonTop: 0,
				buttonBottom: 100,
			};

		const rect = buttonElement.getBoundingClientRect();
		const viewportHeight = window.innerHeight;
		const viewportWidth = window.innerWidth;
		const tooltipHeight = 400;
		const tooltipWidth = 350;

		const spaceBelow = viewportHeight - rect.bottom;
		const spaceAbove = rect.top;
		const showAbove = spaceBelow < tooltipHeight && spaceAbove > tooltipHeight;

		let left = rect.left + rect.width / 2 - tooltipWidth / 2;
		const right = "auto";

		if (left < 10) {
			left = 10;
		} else if (left + tooltipWidth > viewportWidth - 10) {
			left = viewportWidth - tooltipWidth - 10;
		}

		return {
			top: !showAbove,
			left: Math.max(10, left),
			right: right,
			buttonTop: rect.top,
			buttonBottom: rect.bottom,
			showAbove,
		};
	};

	const handleSelectAll = () => {
		if (onSelectAll) {
			onSelectAll(!isAllSelected);
		}
	};

	const handleAudienceSearch = (event) => {
		const value = event.target.value;
		setInternalCurrentPage(1); // ADD THIS LINE

		if (onSearchChange) {
			onSearchChange(value);
		}
	};

	const clearSearch = () => {
		if (onSearchChange) {
			onSearchChange("");
			setInternalCurrentPage(1);
		}
		setIsSearchVisible(false);
		// Optionally clear sort when clearing search
		// setSortColumn(null);
		// setSortDirection('asc');
	};

	const formatNumber = (value) => {
		if (value === null || value === undefined || value === "") return "N/A";
		if (typeof value === "number") {
			return value.toLocaleString();
		}
		if (typeof value === "string" && !isNaN(value)) {
			return parseInt(value).toLocaleString();
		}
		return value;
	};

	const formatPercentage = (value) => {
		if (value === null || value === undefined || value === "") return "N/A";
		if (typeof value === "string" && value.includes("%")) return value;
		if (typeof value === "number") return `${value.toFixed(2)}%`;
		if (typeof value === "string" && !isNaN(value))
			return `${parseFloat(value).toFixed(2)}%`;
		return value;
	};

	const formatCurrency = (value) => {
		if (value === null || value === undefined || value === "") return "N/A";
		if (typeof value === "number") {
			return `${value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
		}
		return value;
	};

	const handleTooltipToggle = (itemId, event) => {
		event.stopPropagation();

		// ✅ NEW: If modal callback is provided, use it instead of tooltip
		if (onOpenAudienceModal) {
			const item = currentPageData.find((i) => i.id === itemId);
			if (item) {
				// Pass the original data structure to the modal
				onOpenAudienceModal(item.originalData || item);
			}
			return;
		}

		// Original tooltip behavior
		if (activeTooltip === itemId) {
			setActiveTooltip(null);
		} else {
			const buttonElement =
				event.currentTarget.querySelector(".info-icon") || event.currentTarget;
			const position = calculateTooltipPosition(buttonElement);
			setTooltipPosition(position);
			setActiveTooltip(itemId);
		}
	};

	const handleExcludeSelected = () => {
		if (onExcludeSelected && selectedResults.length > 0) {
			onExcludeSelected(selectedResults);
		}
	};

	const getCompositeScoreClass = (score) => {
		if (score >= 80) return "excellent";
		if (score >= 70) return "great";
		if (score >= 60) return "acceptable";
		if (score >= 50) return "poor";
		return "irrelevant";
	};

	const getStatusClass = (status) => {
		switch (status?.toUpperCase()) {
			case "PROCESSED":
			case "COMPLETE":
				return "status-success";
			case "PROCESSING":
			case "PENDING":
				return "status-pending";
			case "ERROR":
			case "FAILED":
				return "status-error";
			default:
				return "status-unknown";
		}
	};

	// Audience Metrics Tooltip Component
	const AudienceMetricsTooltip = ({ item, isVisible, position }) => {
		if (!isVisible || !item) return null;

		const tooltipStyle = {
			position: "fixed",
			left: `${position.left}px`,
			zIndex: 999999,
		};

		if (position.showAbove) {
			tooltipStyle.bottom = `${window.innerHeight - position.buttonTop + 10}px`;
		} else {
			tooltipStyle.top = `${position.buttonBottom + 10}px`;
		}

		return (
			<div className="metrics-tooltip" style={tooltipStyle}>
				<div className="tooltip-header">
					<h4>{item.audienceName || "Unknown Audience"}</h4>
				</div>
				<div className="tooltip-content">
					<div className="tooltip-metric-row">
						<span className="tooltip-metric-label">Account:</span>
						<span className="tooltip-metric-value">
							{item.accountName || "N/A"}
						</span>
					</div>
					<div className="tooltip-metric-row">
						<span className="tooltip-metric-label">Data Source:</span>
						<span className="tooltip-metric-value">
							{item.dataSource || "N/A"}
						</span>
					</div>
					<div className="tooltip-metric-row">
						<span className="tooltip-metric-label">Type:</span>
						<span className="tooltip-metric-value">{item.type || "N/A"}</span>
					</div>
					<div className="tooltip-metric-row">
						<span className="tooltip-metric-label">Status:</span>
						<span
							className={`tooltip-metric-value status-badge ${getStatusClass(item.status)}`}
						>
							{item.status || "N/A"}
						</span>
					</div>
					<div className="tooltip-metric-section">
						<h5>Audience Metrics</h5>
						<div className="tooltip-metric-row">
							<span className="tooltip-metric-label">Composite Score:</span>
							<span className="tooltip-metric-value">
								<span
									className={`score ${getCompositeScoreClass(item.compositeScore)}`}
								>
									{item.compositeScore ? item.compositeScore.toFixed(2) : "N/A"}
								</span>
							</span>
						</div>
						<div className="tooltip-metric-row">
							<span className="tooltip-metric-label">Unique Users:</span>
							<span className="tooltip-metric-value">
								{formatNumber(item.metrics?.uniqueUsers)}
							</span>
						</div>
						<div className="tooltip-metric-row">
							<span className="tooltip-metric-label">Requests:</span>
							<span className="tooltip-metric-value">
								{formatNumber(item.metrics?.requests)}
							</span>
						</div>
						<div className="tooltip-metric-row">
							<span className="tooltip-metric-label">Impressions:</span>
							<span className="tooltip-metric-value">
								{formatNumber(item.metrics?.impressions)}
							</span>
						</div>
						<div className="tooltip-metric-row">
							<span className="tooltip-metric-label">Cookies:</span>
							<span className="tooltip-metric-value">
								{formatNumber(item.metrics?.cookies)}
							</span>
						</div>
						<div className="tooltip-metric-row">
							<span className="tooltip-metric-label">Mobile Devices:</span>
							<span className="tooltip-metric-value">
								{formatNumber(item.metrics?.mobileDevices)}
							</span>
						</div>
						<div className="tooltip-metric-row">
							<span className="tooltip-metric-label">Connected TV:</span>
							<span className="tooltip-metric-value">
								{formatNumber(item.metrics?.connectedTvDevices)}
							</span>
						</div>
						<div className="tooltip-metric-row">
							<span className="tooltip-metric-label">Spend:</span>
							<span className="tooltip-metric-value">
								{formatCurrency(item.metrics?.spendInUsd)}
							</span>
						</div>
						<div className="tooltip-metric-row">
							<span className="tooltip-metric-label">Avg eCPM:</span>
							<span className="tooltip-metric-value">
								{formatCurrency(item.metrics?.avgEcpm)}
							</span>
						</div>
						<div className="tooltip-metric-row">
							<span className="tooltip-metric-label">CTR:</span>
							<span className="tooltip-metric-value">
								{formatPercentage(item.metrics?.clickThroughRate)}
							</span>
						</div>
						<div className="tooltip-metric-row">
							<span className="tooltip-metric-label">Viewability:</span>
							<span className="tooltip-metric-value">
								{formatPercentage(item.metrics?.viewability)}
							</span>
						</div>
						<div className="tooltip-metric-row">
							<span className="tooltip-metric-label">Clicks:</span>
							<span className="tooltip-metric-value">
								{formatNumber(item.metrics?.clicks)}
							</span>
						</div>
						<div className="tooltip-metric-row">
							<span className="tooltip-metric-label">Video Starts:</span>
							<span className="tooltip-metric-value">
								{formatNumber(item.metrics?.videoStarts)}
							</span>
						</div>
						<div className="tooltip-metric-row">
							<span className="tooltip-metric-label">Video Completions:</span>
							<span className="tooltip-metric-value">
								{formatNumber(item.metrics?.videoCompletions)}
							</span>
						</div>
					</div>
					<div className="tooltip-metric-row">
						<span className="tooltip-metric-label">Export Targets:</span>
						<span className="tooltip-metric-value tooltip-text-multiline">
							{item.exportTargets || "N/A"}
						</span>
					</div>
					<div className="tooltip-metric-row">
						<span className="tooltip-metric-label">Technical Attributes:</span>
						<span className="tooltip-metric-value tooltip-text-multiline">
							{item.technicalAttributes || "N/A"}
						</span>
					</div>
					<div className="tooltip-metric-row">
						<span className="tooltip-metric-label">Created With:</span>
						<span className="tooltip-metric-value">
							{item.createdWith || "N/A"}
						</span>
					</div>
					<div className="tooltip-metric-row">
						<span className="tooltip-metric-label">Description:</span>
						<span className="tooltip-metric-value tooltip-text-multiline">
							{item.description || "N/A"}
						</span>
					</div>
				</div>
			</div>
		);
	};

	if (isLoading) {
		return (
			<div className="results-container">
				<div className="results-section">
					<h2 className="section-title">Audience Results</h2>
					<div className="loading-state">
						<div className="loading-spinner"></div>
						<p>Loading audience results...</p>
					</div>
				</div>
			</div>
		);
	}

	if (error) {
		return (
			<div className="results-container">
				<div className="results-section">
					<h2 className="section-title">Audience Results</h2>
					<div className="error-state">
						<div className="error-icon">❌</div>
						<h3>Error Loading Results</h3>
						<p>{error}</p>
						<button
							onClick={() => window.location.reload()}
							className="retry-button"
						>
							Retry
						</button>
					</div>
				</div>
			</div>
		);
	}

	return (
		<div className="results-container">
			<div className="results-section">
				<div
					style={{
						display: "flex",
						justifyContent: "space-between",
						alignItems: "center",
						marginBottom: "20px",
					}}
				>
					<h2 className="section-title">
						Audience Results
						<span className="results-count">
							{sortColumn && (
								<span className="sort-status">
									{" • "} Sorted by{" "}
									{sortColumn
										.replace(/([A-Z])/g, " $1")
										.toLowerCase()
										.replace(/^\w/, (c) => c.toUpperCase())}{" "}
									({sortDirection === "asc" ? "A-Z" : "Z-A"})
								</span>
							)}
						</span>
						{sortColumn && (
							<button
								onClick={() => {
									setSortColumn(null);
									setSortDirection("asc");
								}}
								className="clear-sort-btn"
								title="Clear sorting"
							>
								Clear Sort
							</button>
						)}
					</h2>

					{/* View Toggle Button */}
					<button
						onClick={toggleViewMode}
						className="view-toggle-btn"
						title={`Switch to ${viewMode === "tiled" ? "table" : "tiles"} view`}
						style={{
							display: "flex",
							alignItems: "center",
							gap: "6px",
							padding: "8px 12px",
							backgroundColor: "#f8f9fa",
							border: "1px solid #dee2e6",
							borderRadius: "6px",
							cursor: "pointer",
							fontSize: "14px",
							fontWeight: "500",
							color: "#495057",
							transition: "all 0.2s ease",
						}}
						onMouseEnter={(e) => {
							e.target.style.backgroundColor = "#e9ecef";
							e.target.style.borderColor = "#adb5bd";
						}}
						onMouseLeave={(e) => {
							e.target.style.backgroundColor = "#f8f9fa";
							e.target.style.borderColor = "#dee2e6";
						}}
					>
						<ViewToggleIcon isTiled={viewMode === "tiled"} />
						{viewMode === "tiled" ? "Table View" : "Tile View"}
					</button>
				</div>

				{/* Conditional Rendering Based on View Mode */}
				{viewMode === "tiled" ? (
					<RelevanceAudienceResults
						data={completeDataset} // Complete dataset
						allData={completeDataset}
						isLoading={isLoading}
						error={error}
						onOpenModal={onOpenAudienceModal}
					/>
				) : (
					<>
						{/* Table View - Complete table with all columns */}
						<div className="results-table-container">
							<table className="results-table">
								<thead>
									<tr>
										<th className="checkbox-column">
											<input
												type="checkbox"
												checked={isAllSelected}
												onChange={handleSelectAll}
												title="Select All"
											/>
										</th>
										<th className="name-column">
											{isSearchVisible ? (
												<div className="search-header-container">
													<input
														type="text"
														placeholder="Search audiences..."
														value={searchTerm || ""}
														onChange={handleAudienceSearch}
														onClick={(e) => e.stopPropagation()}
														onFocus={(e) => {
															e.stopPropagation();
															e.target.select();
														}}
														onKeyDown={(e) => e.stopPropagation()}
														className="audience-search-input"
														autoFocus
													/>
													<button
														className="close-search-btn"
														onClick={() => {
															clearSearch();
															setIsSearchVisible(false);
														}}
														title="Close search"
													>
														✖
													</button>
												</div>
											) : (
												<div className="audience-header">
													<div
														className={`sortable-header ${sortColumn === "audienceName" ? "active" : ""}`}
														onClick={() => handleSort("audienceName")}
													>
														<span className="header-text">
															Clean Audience Name
														</span>
														<SortIcon
															column="audienceName"
															currentSortColumn={sortColumn}
															sortDirection={sortDirection}
														/>
													</div>
													<button
														className="search-toggle-btn"
														onClick={() => setIsSearchVisible(true)}
														title="Search audiences"
													>
														<SearchIcon />
													</button>
												</div>
											)}
										</th>
										<th className="organization-column">
											<div
												className={`sortable-header ${sortColumn === "organization" ? "active" : ""}`}
												onClick={() => handleSort("organization")}
											>
												<span className="header-text">Organization</span>
												<SortIcon
													column="organization"
													currentSortColumn={sortColumn}
													sortDirection={sortDirection}
												/>
											</div>
										</th>
										<th className="provider-column">
											<div
												className={`sortable-header ${sortColumn === "provider" ? "active" : ""}`}
												onClick={() => handleSort("provider")}
											>
												<span className="header-text">Provider</span>
												<SortIcon
													column="provider"
													currentSortColumn={sortColumn}
													sortDirection={sortDirection}
												/>
											</div>
										</th>
										<th className="composite-score-column">
											<div
												className={`sortable-header ${sortColumn === "compositeScore" ? "active" : ""}`}
												onClick={() => handleSort("compositeScore")}
											>
												<span className="header-text">Composite Score</span>
												<SortIcon
													column="compositeScore"
													currentSortColumn={sortColumn}
													sortDirection={sortDirection}
												/>
											</div>
										</th>
										<th className="search-score-column">
											<div
												className={`sortable-header ${sortColumn === "searchScore" ? "active" : ""}`}
												onClick={() => handleSort("searchScore")}
											>
												<span className="header-text">Search Score</span>
												<SortIcon
													column="searchScore"
													currentSortColumn={sortColumn}
													sortDirection={sortDirection}
												/>
											</div>
										</th>
										<th className="reach-column">
											<div
												className={`sortable-header ${sortColumn === "reach" ? "active" : ""}`}
												onClick={() => handleSort("reach")}
											>
												<span className="header-text">Reach</span>
												<SortIcon
													column="reach"
													currentSortColumn={sortColumn}
													sortDirection={sortDirection}
												/>
											</div>
										</th>
										<th className="avg-ecpm-column">
											<div
												className={`sortable-header ${sortColumn === "avgEcpm" ? "active" : ""}`}
												onClick={() => handleSort("avgEcpm")}
											>
												<span className="header-text">Avg eCPM ($)</span>
												<SortIcon
													column="avgEcpm"
													currentSortColumn={sortColumn}
													sortDirection={sortDirection}
												/>
											</div>
										</th>
										<th className="primary-persona-column">
											<div
												className={`sortable-header ${sortColumn === "primaryPersona" ? "active" : ""}`}
												onClick={() => handleSort("primaryPersona")}
											>
												<span className="header-text">Primary Persona</span>
												<SortIcon
													column="primaryPersona"
													currentSortColumn={sortColumn}
													sortDirection={sortDirection}
												/>
											</div>
										</th>
										<th className="audience-strategy-column">
											<div
												className={`sortable-header ${sortColumn === "audienceStrategy" ? "active" : ""}`}
												onClick={() => handleSort("audienceStrategy")}
											>
												<span className="header-text">Audience Strategy</span>
												<SortIcon
													column="audienceStrategy"
													currentSortColumn={sortColumn}
													sortDirection={sortDirection}
												/>
											</div>
										</th>
										<th className="status-column">
											<div
												className={`sortable-header ${sortColumn === "status" ? "active" : ""}`}
												onClick={() => handleSort("status")}
											>
												<span className="header-text">Status</span>
												<SortIcon
													column="status"
													currentSortColumn={sortColumn}
													sortDirection={sortDirection}
												/>
											</div>
										</th>
										<th className="media-types-column">
											<div
												className={`sortable-header ${sortColumn === "mediaTypes" ? "active" : ""}`}
												onClick={() => handleSort("mediaTypes")}
											>
												<span className="header-text">Media Types</span>
												<SortIcon
													column="mediaTypes"
													currentSortColumn={sortColumn}
													sortDirection={sortDirection}
												/>
											</div>
										</th>
										<th className="notes-column">
											<div
												className={`sortable-header ${sortColumn === "notes" ? "active" : ""}`}
												onClick={() => handleSort("notes")}
											>
												<span className="header-text">Notes/Description</span>
												<SortIcon
													column="notes"
													currentSortColumn={sortColumn}
													sortDirection={sortDirection}
												/>
											</div>
										</th>
										<th className="clicks-column">
											<div
												className={`sortable-header ${sortColumn === "clicks" ? "active" : ""}`}
												onClick={() => handleSort("clicks")}
											>
												<span className="header-text">Clicks</span>
												<SortIcon
													column="clicks"
													currentSortColumn={sortColumn}
													sortDirection={sortDirection}
												/>
											</div>
										</th>
										<th className="impressions-column">
											<div
												className={`sortable-header ${sortColumn === "impressions" ? "active" : ""}`}
												onClick={() => handleSort("impressions")}
											>
												<span className="header-text">Impressions</span>
												<SortIcon
													column="impressions"
													currentSortColumn={sortColumn}
													sortDirection={sortDirection}
												/>
											</div>
										</th>
										<th className="fills-column">
											<div
												className={`sortable-header ${sortColumn === "fills" ? "active" : ""}`}
												onClick={() => handleSort("fills")}
											>
												<span className="header-text">Fills</span>
												<SortIcon
													column="fills"
													currentSortColumn={sortColumn}
													sortDirection={sortDirection}
												/>
											</div>
										</th>
										<th className="video-starts-column">
											<div
												className={`sortable-header ${sortColumn === "videoStarts" ? "active" : ""}`}
												onClick={() => handleSort("videoStarts")}
											>
												<span className="header-text">Video Starts</span>
												<SortIcon
													column="videoStarts"
													currentSortColumn={sortColumn}
													sortDirection={sortDirection}
												/>
											</div>
										</th>
										<th className="video-completions-column">
											<div
												className={`sortable-header ${sortColumn === "videoCompletions" ? "active" : ""}`}
												onClick={() => handleSort("videoCompletions")}
											>
												<span className="header-text">Video Completions</span>
												<SortIcon
													column="videoCompletions"
													currentSortColumn={sortColumn}
													sortDirection={sortDirection}
												/>
											</div>
										</th>
										<th className="viewable-impressions-column">
											<div
												className={`sortable-header ${sortColumn === "viewableImpressions" ? "active" : ""}`}
												onClick={() => handleSort("viewableImpressions")}
											>
												<span className="header-text">
													Viewable Impressions
												</span>
												<SortIcon
													column="viewableImpressions"
													currentSortColumn={sortColumn}
													sortDirection={sortDirection}
												/>
											</div>
										</th>
										<th className="spend-column">
											<div
												className={`sortable-header ${sortColumn === "spend" ? "active" : ""}`}
												onClick={() => handleSort("spend")}
											>
												<span className="header-text">Spend ($)</span>
												<SortIcon
													column="spend"
													currentSortColumn={sortColumn}
													sortDirection={sortDirection}
												/>
											</div>
										</th>
										<th className="avg-spend-per-user-column">
											<div
												className={`sortable-header ${sortColumn === "avgSpendPerUser" ? "active" : ""}`}
												onClick={() => handleSort("avgSpendPerUser")}
											>
												<span className="header-text">
													Avg Spend per User ($)
												</span>
												<SortIcon
													column="avgSpendPerUser"
													currentSortColumn={sortColumn}
													sortDirection={sortDirection}
												/>
											</div>
										</th>
									</tr>
								</thead>
								<tbody>
									{currentPageData && currentPageData.length > 0 ? (
										currentPageData.map((item) => (
											<tr
												key={item.id}
												className={`result-row ${selectedResults.includes(item.id) ? "selected" : ""}`}
											>
												<td className="checkbox-cell">
													<input
														type="checkbox"
														checked={selectedResults.includes(item.id)}
														onChange={() => onResultSelect(item.id)}
													/>
												</td>
												<td
													className={`name-cell ${activeTooltip === item.id ? "tooltip-active" : ""}`}
												>
													<div className="audience-name-container">
														<span
															className="audience-name"
															onMouseEnter={(e) => {
																setHoveredAudienceName(item.audienceName);
																setNameTooltipPosition(
																	calculateNameTooltipPosition(e.currentTarget),
																);
															}}
															onMouseLeave={() => setHoveredAudienceName(null)}
														>
															{item.audienceName}
														</span>
														<div
															className="info-icon-wrapper"
															onClick={(e) => handleTooltipToggle(item.id, e)}
														>
															<button
																className="info-icon"
																title="View detailed metrics"
															>
																i
															</button>
														</div>
													</div>
												</td>
												<td className="organization-cell">
													{item.accountName || "N/A"}
												</td>
												<td className="provider-cell">
													{item.dataSource || "N/A"}
												</td>
												<td className="composite-score-cell">
													<div className="score-bar-container">
														<div
															className={`score-bar ${getCompositeScoreClass(item.compositeScore)}`}
															style={{
																width: `${Math.min(100, item.compositeScore || 0)}%`,
															}}
														></div>
														<span className="score-text">
															{item.compositeScore
																? item.compositeScore.toFixed(2)
																: "N/A"}
														</span>
													</div>
												</td>
												<td className="search-score-cell">
													{calculateRelevanceScore(item).toFixed(4)}
												</td>
												<td className="reach-cell">
													{formatNumber(item.metrics?.uniqueUsers)}
												</td>
												<td className="avg-ecpm-cell">
													{formatCurrency(item.metrics?.avgEcpm)}
												</td>
												<td className="primary-persona-cell">
													{item.primaryPersona || "N/A"}
												</td>
												<td className="audience-strategy-cell">
													{item.audienceStrategy || "Acquisition"}
												</td>
												<td className="status-cell">
													<span
														className={`status-badge ${getStatusClass(item.status)}`}
													>
														{item.status || "PROCESSED"}
													</span>
												</td>
												<td className="media-types-cell">
													{item.exportTargets || "N/A"}
												</td>
												<td
													className="notes-cell"
													title={item.description || "N/A"}
												>
													<span className="notes-text">
														{item.description && item.description.length > 50
															? `${item.description.substring(0, 50)}...`
															: item.description || "N/A"}
													</span>
												</td>
												<td className="clicks-cell">
													{formatNumber(item.metrics?.clicks || 0)}
												</td>
												<td className="impressions-cell">
													{formatNumber(item.metrics?.impressions)}
												</td>
												<td className="fills-cell">
													{formatNumber(item.metrics?.fills || 0)}
												</td>
												<td className="video-starts-cell">
													{formatNumber(item.metrics?.videoStarts || 0)}
												</td>
												<td className="video-completions-cell">
													{formatNumber(item.metrics?.videoCompletions || 0)}
												</td>
												<td className="viewable-impressions-cell">
													{formatNumber(item.metrics?.viewableImpressions || 0)}
												</td>
												<td className="spend-cell">
													{formatCurrency(item.metrics?.spendInUsd / 1000000)}
												</td>
												<td className="avg-spend-per-user-cell">
													{formatCurrency(item.metrics?.avgSpendPerUser || 0)}
												</td>
											</tr>
										))
									) : (
										<tr className="empty-results-row">
											<td colSpan={21} className="empty-results-cell">
												<div className="empty-results-content">
													<h3>No Results Found</h3>
													<p>
														{searchTerm
															? `No results match "${searchTerm}". Try adjusting your search.`
															: "No audience data available. Try running a search or adjusting your filters."}
													</p>
													{searchTerm && (
														<button
															onClick={clearSearch}
															className="clear-search-button"
														>
															Clear Search
														</button>
													)}
												</div>
											</td>
										</tr>
									)}
								</tbody>
							</table>
						</div>

						{/* ✅ FIXED: Pagination with internal state management - like AppResultsList */}
						{displayTotalItems > 0 && displayTotalPages > 1 && (
							<div className="pagination-wrapper">
								<Pagination
									currentPage={internalCurrentPage}
									totalPages={displayTotalPages}
									totalItems={displayTotalItems}
									itemsPerPage={internalPageSize}
									onPageChange={handleInternalPageChange}
									onPageSizeChange={handleInternalPageSizeChange}
									loading={false}
									showJumpToPage={true}
									showPageSizeSelector={true}
									showResultsInfo={true}
									forceShow={displayTotalItems > 0}
									excludedCount={excludedItems.length}
								/>
							</div>
						)}

						{/* Action Buttons - Only show in table mode */}
						<div className="action-buttons">
							<button
								type="button"
								className="exclude-btn"
								disabled={
									selectedResults.length === 0 ||
									!currentPageData ||
									currentPageData.length === 0
								}
								onClick={handleExcludeSelected}
							>
								Exclude Selected ({selectedResults.length})
							</button>
							<EnhancedAudienceExportDropdown
								filteredData={getFilteredAndSortedData()}
								isExporting={isExporting}
								onExport={onExport} // This should call the enhanced exportAudienceToCSV
								totalCount={getCalculatedTotalItems()}
								selectedCount={selectedResults.length}
							/>
						</div>
					</>
				)}
			</div>

			{/* Tooltip - Only show in table mode */}
			{activeTooltip && viewMode === "table" && (
				<AudienceMetricsTooltip
					item={currentPageData.find((item) => item.id === activeTooltip)}
					isVisible={activeTooltip !== null}
					position={tooltipPosition}
				/>
			)}

			{/* ✅ ADD NAME HOVER TOOLTIP */}
			{hoveredAudienceName && viewMode === "table" && (
				<div
					className="audience-name-tooltip"
					style={{
						position: "fixed",
						top: `${nameTooltipPosition.top}px`,
						left: `${nameTooltipPosition.left}px`,
						zIndex: 999999,
					}}
				>
					{hoveredAudienceName}
				</div>
			)}

			{/* Styles - same as before */}
			<style jsx>{`
                .results-section {
                    overflow: visible;
                    position: relative;
                    z-index: 1;
                }

                .section-title {
                    display: flex;
                    align-items: center;
                    gap: 10px;
                    margin-bottom: 20px;
                    font-size: 18px;
                    font-weight: 600;
                    color: #212529;
                }

                .results-count {
                    font-size: 14px;
                    color: #666;
                    font-weight: normal;
                }

                .sort-status {
                    color: #0076BD;
                    font-weight: 500;
                }

                .clear-sort-btn {
                    font-size: 12px;
                    color: #6c757d;
                    background: none;
                    border: 1px solid #dee2e6;
                    border-radius: 4px;
                    padding: 4px 8px;
                    cursor: pointer;
                    transition: all 0.2s ease;
                    margin-left: 8px;
                    font-weight: normal;
                }

                .clear-sort-btn:hover {
                    color: #495057;
                    border-color: #adb5bd;
                    background-color: #f8f9fa;
                }

                .view-toggle-btn {
                    transition: all 0.2s ease;
                }

                .results-table-container {
                    overflow-x: auto;
                    overflow-y: visible;
                    position: static;
                    border: 1px solid #dee2e6;
                    border-radius: 8px;
                    background-color: white;
                    box-shadow: 0 2px 4px rgba(0,0,0,0.1);
                    margin-bottom: 20px;
                    max-width: 100%;
                }

                .results-table {
                    position: static;
                    width: 100%;
                    border-collapse: collapse;
                    table-layout: fixed;
                    font-size: 14px;
                    min-width: 2400px;
                }

                /* Column Widths */
                .results-table .checkbox-column { width: 40px; }
                .results-table .name-column { width: 200px; }
                .results-table .organization-column { width: 120px; }
                .results-table .provider-column { width: 100px; }
                .results-table .composite-score-column { width: 120px; }
                .results-table .search-score-column { width: 100px; }
                .results-table .reach-column { width: 100px; }
                .results-table .sold-impressions-column { width: 120px; }
                .results-table .avg-ecpm-column { width: 100px; }
                .results-table .primary-persona-column { width: 130px; }
                .results-table .audience-strategy-column { width: 120px; }
                .results-table .status-column { width: 100px; }
                .results-table .media-types-column { width: 150px; }
                .results-table .notes-column { width: 200px; }
                .results-table .clicks-column { width: 80px; }
                .results-table .impressions-column { width: 140px; }
                .results-table .fills-column { width: 120px; }
                .results-table .video-starts-column { width: 100px; }
                .results-table .video-completions-column { width: 120px; }
                .results-table .viewable-impressions-column { width: 130px; }
                .results-table .spend-column { width: 100px; }
                .results-table .avg-spend-per-user-column { width: 130px; }

                .results-table th {
                    background-color: #f8f9fa;
                    border-bottom: 2px solid #dee2e6;
                    padding: 12px 8px;
                    text-align: left;
                    font-weight: 600;
                    font-size: 12px;
                    color: #495057;
                    white-space: nowrap;
                    position: static;
                    overflow: hidden;
                }

                .results-table td {
                    padding: 12px 8px;
                    border-bottom: 1px solid #e9ecef;
                    vertical-align: middle;
                    text-overflow: ellipsis;
                    white-space: nowrap;
                    position: static;
                    overflow: hidden;
                }

                .checkbox-cell, .checkbox-column {
                    text-align: center;
                    padding: 8px;
                }

                .composite-score-cell, .composite-score-column,
                .search-score-cell, .search-score-column,
                .reach-cell, .reach-column,
                .sold-impressions-cell, .sold-impressions-column,
                .avg-ecpm-cell, .avg-ecpm-column,
                .clicks-cell, .clicks-column,
                .impressions-cell, .impressions-column,
                .fills-cell, .fills-column,
                .video-starts-cell, .video-starts-column,
                .video-completions-cell, .video-completions-column,
                .viewable-impressions-cell, .viewable-impressions-column,
                .spend-cell, .spend-column,
                .avg-spend-per-user-cell, .avg-spend-per-user-column,
                .status-cell, .status-column {
                    text-align: center;
                }

                /* Score Bar Styling */
                .score-bar-container {
                    position: relative;
                    width: 100%;
                    height: 20px;
                    background-color: #f8f9fa;
                    border-radius: 10px;
                    overflow: hidden;
                    display: flex;
                    align-items: center;
                }

                .score-bar {
                    height: 100%;
                    transition: width 0.3s ease;
                    border-radius: 10px;
                }

                .score-bar.excellent { 
                    background-color: #28a745;
                }
                .score-bar.great { 
                    background-color: #17a2b8;
                }
                .score-bar.acceptable { 
                    background-color: #ffc107;
                }
                .score-bar.poor { 
                    background-color: #fd7e14;
                }
                .score-bar.irrelevant { 
                    background-color: #6c757d;
                }

                .score-text {
                    position: absolute;
                    left: 50%;
                    top: 50%;
                    transform: translate(-50%, -50%);
                    font-size: 11px;
                    font-weight: 600;
                    color: #212529;
                    text-shadow: 0 1px 2px rgba(255,255,255,0.8);
                    z-index: 1;
                }

                /* Notes cell styling */
                .notes-cell {
                    max-width: 200px;
                }

                .notes-text {
                    display: block;
                    white-space: nowrap;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    font-size: 12px;
                    color: #6c757d;
                }

                .audience-name-container {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    position: static;
                    min-width: 0;
                }

                .audience-name {
                    font-weight: 500;
                    color: #212529;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    white-space: nowrap;
                    flex: 1;
                    cursor: help; 
                }

                .audience-name-tooltip {
                    background: #2c3e50;
                    color: white;
                    padding: 8px 12px;
                    border-radius: 6px;
                    font-size: 13px;
                    font-weight: 500;
                    max-width: 400px;
                    word-wrap: break-word;
                    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
                    pointer-events: none;
                    white-space: normal;
                    line-height: 1.4;
                    z-index: 999999;
                }

                .audience-header {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    width: 100%;
                    min-width: 0;
                }

                .sortable-header {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    cursor: pointer;
                    user-select: none;
                    transition: all 0.2s;
                    padding: 2px 4px;
                    border-radius: 3px;
                    flex: 1;
                    min-width: 0;
                }

                .sortable-header:hover {
                    background-color: rgba(0, 118, 189, 0.1);
                }

                .sortable-header.active {
                    background-color: rgba(0, 118, 189, 0.15);
                    color: #0076BD;
                    font-weight: 600;
                }

                .sortable-header .header-text {
                    flex: 1;
                    min-width: 0;
                }

                .sort-icon {
                    display: inline-flex;
                    align-items: center;
                    margin-left: 4px;
                    opacity: 0.6;
                    transition: opacity 0.2s;
                    flex-shrink: 0;
                }

                .sortable-header:hover .sort-icon,
                .sortable-header.active .sort-icon {
                    opacity: 1;
                }

                .sort-icon svg {
                    width: 12px;
                    height: 12px;
                }

                .dropdown-item {
                width: '100%',
    padding: 8px 12px;
    border: none;
    background: none;
    textAlign: left;
    cursor: pointer;
    fontSize: 14px;
    color: #212529;
    transition: background-color 0.2s;
                }

                .search-toggle-btn {
                    background: none;
                    border: none;
                    cursor: pointer;
                    font-size: 12px;
                    padding: 4px;
                    border-radius: 3px;
                    transition: background-color 0.2s;
                    flex-shrink: 0;
                    margin-left: 4px;
                }

                .search-toggle-btn:hover {
                    background-color: rgba(0,0,0,0.1);
                }

                .search-header-container {
                    position: relative;
                    display: flex;
                    align-items: center;
                    width: 100%;
                }

                .audience-search-input {
                    width: 100%;
                    padding: 3px 20px 3px 6px;
                    border: 1px solid #d1d5db;
                    border-radius: 3px;
                    font-size: 11px;
                    background-color: white;
                    transition: border-color 0.2s;
                    height: 22px;
                    line-height: 1;
                }

                .audience-search-input:focus {
                    outline: none;
                    border-color: #0076BD;
                    box-shadow: 0 0 0 2px rgba(0, 118, 189, 0.1);
                }

                .close-search-btn {
                    position: absolute;
                    right: 3px;
                    top: 50%;
                    transform: translateY(-50%);
                    background: none;
                    border: none;
                    color: #6b7280;
                    cursor: pointer;
                    font-size: 14px;
                    line-height: 1;
                    padding: 1px 2px;
                    border-radius: 2px;
                    transition: color 0.2s;
                    height: 16px;
                    width: 16px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }

                .close-search-btn:hover {
                    color: #374151;
                    background-color: #f3f4f6;
                }

                .score {
                    display: inline-block;
                    padding: 4px 8px;
                    border-radius: 4px;
                    font-weight: 600;
                    font-size: 12px;
                    background-color: #f8f9fa;
                }

                .score.excellent { 
                    background-color: #d4edda; 
                    color: #155724; 
                }
                .score.great { 
                    background-color: #d1ecf1; 
                    color: #0c5460; 
                }
                .score.acceptable { 
                    background-color: #fff3cd; 
                    color: #856404; 
                }
                .score.poor { 
                    background-color: #f8d7da; 
                    color: #721c24; 
                }
                .score.irrelevant { 
                    background-color: #f5f5f5; 
                    color: #6c757d; 
                }

                .status-badge {
                    display: inline-block;
                    padding: 3px 8px;
                    border-radius: 12px;
                    font-size: 11px;
                    font-weight: 500;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                }

                .status-success {
                    background-color: #d4edda;
                    color: #155724;
                }

                .status-pending {
                    background-color: #fff3cd;
                    color: #856404;
                }

                .status-error {
                    background-color: #f8d7da;
                    color: #721c24;
                }

                .status-unknown {
                    background-color: #e2e3e5;
                    color: #6c757d;
                }

                .result-row {
                    transition: background-color 0.2s ease;
                }

                .result-row:hover {
                    background-color: #f8f9fa;
                }

                .result-row.selected {
                    background-color: #e3f2fd;
                }

                .info-icon-wrapper {
                    position: static;
                    display: inline-flex;
                    flex-shrink: 0;
                }

                .info-icon {
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    width: 18px;
                    height: 18px;
                    border-radius: 50%;
                    background-color: #6c757d;
                    color: white;
                    border: none;
                    font-size: 11px;
                    font-weight: bold;
                    cursor: pointer;
                    transition: all 0.2s;
                }

                .info-icon:hover {
                    background-color: #495057;
                    color: white;
                }

                .metrics-tooltip {
                    background: #0076BD;
                    color: white;
                    border-radius: 8px;
                    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
                    min-width: 350px;
                    max-width: 400px;
                    max-height: 400px;
                    overflow-y: auto;
                    pointer-events: auto;
                }

                .tooltip-header {
                    padding: 12px 16px;
                    border-bottom: 1px solid rgba(255, 255, 255, 0.2);
                    border-radius: 8px 8px 0 0;
                }

                .tooltip-header h4 {
                    margin: 0;
                    font-size: 14px;
                    font-weight: 600;
                    color: white;
                }

                .tooltip-content {
                    padding: 16px;
                }

                .tooltip-metric-section {
                    margin-top: 16px;
                }

                .tooltip-metric-section h5 {
                    margin: 0 0 10px 0;
                    font-size: 13px;
                    font-weight: 600;
                    color: white;
                    border-bottom: 1px solid rgba(255, 255, 255, 0.2);
                    padding-bottom: 6px;
                }

                .tooltip-metric-row {
                    display: flex;
                    justify-content: space-between;
                    align-items: flex-start;
                    padding: 6px 0;
                    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
                    gap: 8px;
                }

                .tooltip-metric-row:last-child {
                    border-bottom: none;
                }

                .tooltip-metric-label {
                    font-weight: 400;
                    color: rgba(255, 255, 255, 0.9);
                    font-size: 12px;
                    flex-shrink: 0;
                    min-width: 120px;
                }

                .tooltip-metric-value {
                    font-weight: 500;
                    color: white;
                    font-size: 12px;
                }

                .tooltip-text-multiline {
                    white-space: normal;
                    word-wrap: break-word;
                    line-height: 1.4;
                    max-width: 200px;
                    text-align: left;
                }

                /* Pagination wrapper */
                .pagination-wrapper {
                    width: 100%;
                    max-width: 100%;
                    overflow-x: auto;
                    overflow-y: visible;
                    margin-bottom: 20px;
                }

                .action-buttons {
                    display: flex;
                    gap: 12px;
                    align-items: center;
                    margin-top: 20px;
                    padding: 16px;
                    background-color: #f8f9fa;
                    border-radius: 8px;
                    border: 1px solid #dee2e6;
                }

                .btn-medium-secondary {
                    padding: 8px 16px;
                    background-color: #1FBAB0;
                    color: white;
                    border: none;
                    border-radius: 4px;
                    font-weight: 500;
                    cursor: pointer;
                    transition: background-color 0.2s;
                }

                .btn-medium-secondary:hover:not(:disabled) {
                    background-color: #199f98;
                }

                .btn-medium-secondary:disabled {
                    background-color: rgb(24, 176, 179);
                    cursor: not-allowed;
                }

                .exclude-btn {
                    padding: 8px 16px;
                    background-color: #cb2816ff;
                    color: white;
                    border: none;
                    border-radius: 4px;
                    font-weight: 500;
                    cursor: pointer;
                    transition: background-color 0.2s;
                }

                .exclude-btn:hover:not(:disabled) {
                    background-color: #f2a09cff;
                }

                .exclude-btn:disabled {
                    background-color: #f2a09cff;
                    cursor: not-allowed;
                }

                .loading-state {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    padding: 60px 20px;
                    text-align: center;
                }

                .loading-spinner {
                    width: 40px;
                    height: 40px;
                    border: 4px solid #f3f3f3;
                    border-top: 4px solid #0076BD;
                    border-radius: 50%;
                    animation: spin 1s linear infinite;
                    margin-bottom: 20px;
                }

                @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                }

                .error-state {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    padding: 60px 20px;
                    text-align: center;
                    color: #dc3545;
                }

                .error-icon {
                    font-size: 48px;
                    margin-bottom: 20px;
                }

                .retry-button {
                    padding: 10px 20px;
                    background-color: #dc3545;
                    color: white;
                    border: none;
                    border-radius: 4px;
                    cursor: pointer;
                    margin-top: 15px;
                }

                .retry-button:hover {
                    background-color: #c82333;
                }

                .empty-results-row {
                    background-color: #f8f9fa;
                }

                .empty-results-cell {
                    padding: 60px 20px !important;
                    text-align: center;
                    border: none;
                    vertical-align: middle;
                }

                .empty-results-content {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    color: #666;
                }

                .empty-results-content h3 {
                    margin: 0 0 15px 0;
                    font-size: 18px;
                    color: #333;
                }

                .empty-results-content p {
                    margin: 0 0 15px 0;
                    color: #666;
                    line-height: 1.5;
                    max-width: 400px;
                }

                .clear-search-button {
                    padding: 8px 16px;
                    background-color: #6c757d;
                    color: white;
                    border: none;
                    border-radius: 4px;
                    cursor: pointer;
                    margin-top: 15px;
                }

                .clear-search-button:hover {
                    background-color: #545b62;
                }

                @media (max-width: 1600px) {
                    .results-table {
                        min-width: 2200px;
                    }
                }

                @media (max-width: 1400px) {
                    .results-table {
                        min-width: 2000px;
                        font-size: 13px;
                    }
                    .results-table .name-column { width: 180px; }
                    .results-table .organization-column { width: 100px; }
                    .results-table .notes-column { width: 150px; }
                }

                @media (max-width: 1200px) {
                    .results-table {
                        min-width: 1800px;
                        font-size: 12px;
                    }
                    .results-table th,
                    .results-table td {
                        padding: 8px 6px;
                    }
                    .results-table .name-column { width: 160px; }
                    .results-table .notes-column { width: 120px; }
                }

                @media (max-width: 768px) {
                    .results-table {
                        min-width: 1600px;
                        font-size: 11px;
                    }
                    .results-table th,
                    .results-table td {
                        padding: 6px 4px;
                    }
                    
                    .metrics-tooltip {
                        min-width: 90vw;
                        max-width: 90vw;
                        left: 5vw !important;
                        right: auto !important;
                    }
                    
                    .section-title {
                        font-size: 16px;
                        flex-direction: column;
                        align-items: flex-start;
                        gap: 4px;
                    }
                }

                @media (max-width: 480px) {
                    .results-table {
                        min-width: 1400px;
                        font-size: 10px;
                    }
                    .results-table th,
                    .results-table td {
                        padding: 4px 2px;
                    }
                    
                    .action-buttons {
                        flex-direction: column;
                        align-items: stretch;
                        gap: 8px;
                    }
                }
            `}</style>
		</div>
	);
};
